import { Divider, Group, Stack, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { getFullName } from '~/components/utils/formatters';
import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import MessageBanner, { BannerType } from '~/domains/common/components/MessageBanner/MessageBanner';
import { formatDollars } from '~/domains/common/utils/formatters';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import dt from '~/testing';

import { WIRE_DEPOSIT_MINIMUM } from '../../constants';

import { BackButton } from './BackButton';
import { NumberedRow } from './NumberedRow';

// TODO([EDGE-28259]): bank details are hardcoded in the locale file — confirm production values with finance
const BANK_DETAIL_KEYS = [
  'receivingBankName',
  'receivingBankAddress',
  'routingNumber',
  'accountNumber',
  'beneficiaryTitle',
  'beneficiaryAddress',
] as const;

const NOTICE_KEYS = ['minimum', 'nameMatch', 'deadline', 'location'] as const;

type WireNoticeProps = {
  noticeKey: (typeof NOTICE_KEYS)[number];
  index: number;
  formattedMinimum: string;
  accountName: string;
};

function WireNotice({ noticeKey, index, formattedMinimum, accountName }: WireNoticeProps) {
  const { t } = useTranslation('payments');

  return (
    <NumberedRow number={index + 1} align="flex-start">
      {t(
        `deposits.wireTransfer.notices.${noticeKey}`,
        noticeKey === 'minimum' ? { amount: formattedMinimum } : undefined
      )}
      {noticeKey === 'nameMatch' && accountName.length > 0 && (
        <Text
          data-test-id={dt.payments.deposits.wireInstructionsSection.accountName}
          tag="span"
          variant="subhead-small"
        >
          {': '}
          {accountName}
        </Text>
      )}
    </NumberedRow>
  );
}

type WireInstructionsSectionProps = {
  onBack: () => void;
};

export function WireInstructionsSection({ onBack }: WireInstructionsSectionProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();
  const { user, userDetails } = useContext(UserContext);

  const accountName = getFullName(userDetails?.firstName, userDetails?.lastName);
  const formattedMinimum = formatDollars(WIRE_DEPOSIT_MINIMUM);

  return (
    <Stack
      spacing={theme.other.spacing._32}
      data-test-id={dt.payments.deposits.wireInstructionsSection.container}
    >
      <div>
        <BackButton onClick={onBack} />
        <SignUpHeader
          title={t('deposits.wireTransfer.title')}
          copy={t('deposits.wireTransfer.intro')}
          isLogoVisible={false}
        />
      </div>

      <MessageBanner
        type={BannerType.WARNING}
        title={t('deposits.wireTransfer.warningTitle', { amount: formattedMinimum })}
        message={t('deposits.wireTransfer.warningBody', { amount: formattedMinimum })}
      />

      <Stack spacing={theme.other.spacing._16}>
        <Title variant="subhead-medium">{t('deposits.wireTransfer.importantInformation')}</Title>
        <Stack spacing={theme.other.spacing._12}>
          {NOTICE_KEYS.map((key, index) => (
            <WireNotice
              key={key}
              noticeKey={key}
              index={index}
              formattedMinimum={formattedMinimum}
              accountName={accountName}
            />
          ))}
        </Stack>
      </Stack>

      <Stack spacing={theme.other.spacing._16}>
        <Title variant="subhead-medium">{t('deposits.wireTransfer.bankInformation')}</Title>
        <Stack spacing={theme.other.spacing._8}>
          {BANK_DETAIL_KEYS.map((key) => (
            <Group key={key} position="apart" spacing={theme.other.spacing._8} noWrap>
              <Text variant="body-medium" c="gray2.6">
                {t(`deposits.wireTransfer.bank.${key}.label`)}
              </Text>
              <Text variant="subhead-small" ta="right">
                {t(`deposits.wireTransfer.bank.${key}.value`)}
              </Text>
            </Group>
          ))}
          <Group position="apart" spacing={theme.other.spacing._8} noWrap>
            <Text variant="body-medium" c="gray2.6">
              {t('deposits.wireTransfer.bank.reference.label')}
            </Text>
            <Text
              data-test-id={dt.payments.deposits.wireInstructionsSection.referenceEmail}
              variant="subhead-small"
              ta="right"
            >
              {user?.email || t('deposits.wireTransfer.bank.reference.fallback')}
            </Text>
          </Group>
        </Stack>
      </Stack>

      <Stack spacing={theme.other.spacing._12}>
        <Divider />
        <Text variant="body-small" c="gray2.6" ta="center">
          {t('deposits.wireTransfer.closing')}
        </Text>
        <Button
          data-test-id={dt.payments.deposits.wireInstructionsSection.doneButton}
          type="button"
          variant="primary-fill"
          size="large"
          onClick={onBack}
          fullWidth
        >
          {t('deposits.wireTransfer.done')}
        </Button>
      </Stack>
    </Stack>
  );
}
